/**
 * The call-off "Parts" row filter, shared by every grid that offers it: the
 * Add Warehouse / Direct Delivery grids, the customer Stock Planning grid and
 * the supplier Current Stock grid.
 *
 * A row is "part"-prefixed when its part number or its description starts
 * with the literal "part" (case-insensitive). CR-7717 adds the inverse of the
 * original hide option, so a user can list those rows on their own.
 */

/** 'all' = no filter; 'hide_part' = drop prefixed rows; 'only_part' = keep only them. */
export type PartPrefixFilter = 'all' | 'hide_part' | 'only_part';

export const PART_PREFIX_FILTER_OPTIONS: ReadonlyArray<{ value: PartPrefixFilter; label: string }> = [
  { value: 'all', label: 'Show all parts' },
  { value: 'hide_part', label: 'Hide “part”-prefixed' },
  { value: 'only_part', label: 'Show “part”-prefixed only' },
];

/**
 * Whether a row counts as "part"-prefixed.
 *
 * @param partNumber - The row's part number / item code
 * @param description - The row's description
 * @returns True when either value starts with "part", ignoring case
 */
export function isPartPrefixed(partNumber?: string | null, description?: string | null): boolean {
  return (partNumber || '').toLowerCase().startsWith('part')
    || (description || '').toLowerCase().startsWith('part');
}

/**
 * Whether a row passes the Parts filter.
 *
 * @param filter - The selected Parts option
 * @param partNumber - The row's part number / item code
 * @param description - The row's description
 * @returns True when the row should be shown
 */
export function matchesPartPrefixFilter(
  filter: PartPrefixFilter,
  partNumber?: string | null,
  description?: string | null,
): boolean {
  if (filter === 'all') return true;
  const prefixed = isPartPrefixed(partNumber, description);
  return filter === 'hide_part' ? !prefixed : prefixed;
}

/**
 * Whether a free-text search matches a row's part number or description.
 *
 * @param term - The search text; blank matches everything
 * @param partNumber - The row's part number / item code
 * @param description - The row's description
 * @returns True when either value contains the term, ignoring case
 */
export function matchesPartSearch(
  term: string,
  partNumber?: string | null,
  description?: string | null,
): boolean {
  const needle = term.trim().toLowerCase();
  if (!needle) return true;
  return (partNumber || '').toLowerCase().includes(needle)
    || (description || '').toLowerCase().includes(needle);
}
